// F16 — the FAQ, cut to the six questions a restaurant actually asks.
//
// What was deleted here, and why:
//
// - The claim that basic setup completes in under ten minutes. Entering a real
//   menu is a working session; the claim was false and it appeared on three
//   pages.
// - Everything describing a device you download to a laptop or desktop.
//   Electron is legacy; the product is two native apps plus a browser portal.
// - The Basic / Pro / Premium plan ladder, multi-location support, quarterly
//   business reviews, plan upgrades and downgrades. None of those tiers are
//   sold. Pricing is one price per venue.
// - "Average response time: Under 2 hours during business days", 24/7 support,
//   emergency phone support, "enterprise-grade security", "automatic backups",
//   accounting integrations and a printer compatibility list. Every one was a
//   commitment nothing currently backs.
// - "Most waiters are taking orders confidently within their first shift" and
//   the first-week support promise — invented, and the business is pre-pilot.
// - The connectivity answer. This went through TWO wrong versions before the
//   truth. It originally implied the system runs offline. That was replaced
//   with "holds a local copy and rides out a short drop" — also false, and
//   caught on review of the staging build.
//
//   The native apps are ONLINE-ONLY. There is no local data path: neither
//   host adapter (browser or Capacitor) has a local database, SyncContext's
//   probe reports the backend's real reachability rather than assuming it,
//   and pos-app/ConnectionStatus.js states outright that it "queues nothing
//   and mutates nothing". Local SQLite only ever existed on the legacy
//   Electron app, which is gone — native is the product now.
//
//   So the answer says a connection is required. Overstating resilience is
//   the worst possible thing to be wrong about — a venue that believes it can
//   trade through an outage discovers otherwise mid-service.
//
// Section order is now: hero, the accordion, one closing CTA.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import LandingLayout from '../../components/landing/LandingLayout';
import { useLanguage } from '../../context/LanguageContext';
import { C, A, TYPE, SP, R, W, S } from '../../components/landing/tokens';
import ArchMark from '../../components/landing/ArchMark';

const FAQPage = () => {
    const { t } = useLanguage();

    // -1 closes everything; the first question opens on arrival so the pattern
    // is legible without a click.
    const [openIndex, setOpenIndex] = useState(0);

    const faqs = [
        {
            q: t('What devices does Aqana run on?'),
            a: t('Aqana POS and Aqana Manager are on the App Store for iPhone and iPad. The Android versions are with Google for review. The full back office runs in any browser, on any computer.'),
        },
        {
            q: t('Do I need an internet connection?'),
            a: t('Yes. Aqana needs a working connection to take orders. If it drops, the app shows a reconnecting state and keeps checking, then carries on as soon as the connection is back. We would rather tell you that plainly than have you find out mid-service, so a reliable connection in the venue matters.'),
        },
        {
            q: t('What does it cost?'),
            a: t('A single monthly subscription for the software, quoted per venue. It covers every till and every member of staff, with no separate licence for either. It does not include hardware — you run Aqana on devices you already own. Get in touch and we will quote for your venue.'),
        },
        {
            q: t('Can I use my own card terminal?'),
            a: t('Yes. Aqana records how each bill was paid but does not process the card itself, so you keep your terminal and your existing rates with your payment provider.'),
        },
        {
            q: t('Does it work in other languages?'),
            a: t('Yes. Aqana is multilingual: the whole interface is translated, not just parts of it, and anyone can switch language at any time on the till and in the back office. Layouts mirror properly for right-to-left scripts. Tell us which languages your team needs.'),
        },
        {
            q: t('What happens to my data if I stop using Aqana?'),
            a: (
                <>
                    {t('Your data stays yours. You can export your sales, products and staff records at any point, and you can ask us to delete the account and everything in it.')}{' '}
                    <Link to="/data-deletion" style={{ color: C.ink, textDecoration: 'none', borderBlockEnd: `1px solid ${C.lineStrong}` }}>
                        {t('The request form is on our data deletion page.')}
                    </Link>
                </>
            ),
        },
    ];

    const toggle = (index) => setOpenIndex(openIndex === index ? -1 : index);

    return (
        <LandingLayout>
            {/* ---------- 1. Hero ---------- */}
            <section style={S.section()}>
                <div style={S.container}>
                    <div style={{ maxWidth: 760 }}>
                        <h1 style={{ ...TYPE.displayXl, color: C.ink, margin: 0, textWrap: 'balance' }}>
                            {t('Questions, answered.')}
                        </h1>
                        <p style={{ ...TYPE.bodyL, color: C.inkMuted, maxWidth: 560, marginBlock: `${SP.lg}px ${SP['2xl']}px` }}>
                            {t('The six things restaurants ask us first. If yours is not here, ask us directly and we will answer it plainly.')}
                        </p>
                        <Link to="/contact-us" style={S.btnPrimary}>{t('Ask a question')}</Link>
                    </div>
                </div>
            </section>

            {/* ---------- 2. The accordion ---------- */}
            <section style={S.section({ deep: true })}>
                <div style={{ ...S.container, maxWidth: W.prose }}>
                    <span style={S.label}>{t('COMMON QUESTIONS')}</span>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: SP.md, marginBlockStart: SP.xl }}>
                        {faqs.map((faq, index) => {
                            const isOpen = openIndex === index;
                            const questionId = `faq-question-${index}`;
                            const panelId = `faq-panel-${index}`;

                            return (
                                <div
                                    key={faq.q}
                                    style={{
                                        background: C.surface,
                                        border: `1px solid ${isOpen ? C.lineStrong : C.line}`,
                                        borderRadius: R.md,
                                        transition: 'border-color .18s ease',
                                    }}
                                >
                                    <button
                                        type="button"
                                        id={questionId}
                                        aria-expanded={isOpen}
                                        aria-controls={panelId}
                                        onClick={() => toggle(index)}
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: SP.lg,
                                            background: 'none',
                                            border: 'none',
                                            borderRadius: R.md,
                                            paddingBlock: SP.lg,
                                            paddingInline: SP.xl,
                                            textAlign: 'start',
                                            cursor: 'pointer',
                                            color: C.ink,
                                        }}
                                    >
                                        <span style={{ ...TYPE.title, color: C.ink }}>{faq.q}</span>
                                        <svg
                                            width="18"
                                            height="18"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            aria-hidden="true"
                                            style={{
                                                flexShrink: 0,
                                                color: C.inkFaint,
                                                transform: isOpen ? 'rotate(180deg)' : 'none',
                                                transition: 'transform .18s ease',
                                            }}
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    <div
                                        id={panelId}
                                        role="region"
                                        aria-labelledby={questionId}
                                        hidden={!isOpen}
                                        style={{
                                            paddingInline: SP.xl,
                                            paddingBlockEnd: SP.xl,
                                        }}
                                    >
                                        <div style={{ borderBlockStart: `1px solid ${C.line}`, paddingBlockStart: SP.lg }}>
                                            <p style={{ ...TYPE.body, color: C.inkMuted, margin: 0 }}>{faq.a}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ---------- 3. Closing CTA ---------- */}
            <section
                style={{
                    ...S.section(),
                    background: C.ink,
                    color: C.paper,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                <ArchMark
                    size={420}
                    color={C.paper}
                    opacity={0.05}
                    style={{ position: 'absolute', insetInlineEnd: -60, insetBlockStart: -40, pointerEvents: 'none' }}
                />
                <div style={{ ...S.container, position: 'relative', textAlign: 'center' }}>
                    <h2 style={{ ...TYPE.displayL, color: C.paper, margin: 0, textWrap: 'balance' }}>
                        {t('Still stuck on something?')}
                    </h2>
                    <p style={{ ...TYPE.bodyL, color: A.paperMuted, maxWidth: 520, marginInline: 'auto', marginBlock: `${SP.lg}px ${SP['2xl']}px` }}>
                        {t('Tell us how your venue runs and we will tell you straight whether Aqana fits it.')}
                    </p>
                    <Link
                        to="/contact-us"
                        style={{ ...S.btnPrimary, background: C.paper, color: C.ink, border: `1px solid ${C.paper}` }}
                    >
                        {t('Contact us')}
                    </Link>
                </div>
            </section>
        </LandingLayout>
    );
};

export default FAQPage;
